import { mergeMap as _observableMergeMap, catchError as _observableCatch } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, of as _observableOf } from 'rxjs';
import { Injectable, Inject, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import * as moment from 'moment';
import { API_BASE_URL } from './service-proxies';

function blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
        if (!blob) {
            observer.next('');
            observer.complete();
        } else {
            const reader = new FileReader();
            reader.onload = function () {
                observer.next(this.result);
                observer.complete();
            };
            reader.readAsText(blob);
        }
    });
}

export interface ExceededAmountByCurrencyDto {
    currencyCode: string;
    exceededAmount: number;
}

export interface TlmRecentEventDto {
    tenantId: number;
    tenantName?: string;
    channelId?: number;
    channelName?: string;
    currencyCode: string;
    limitType: string;
    transactionAmount: number;
    limitAmount: number;
    externalTransactionId: string;
    createdTime: string;
}

export interface TlmDashboardDto {
    merchantsExceededLimitToday: number;
    limitExceededEventsToday: number;
    globalLimitExceededToday: number;
    channelLimitExceededToday: number;
    thresholdAlertsToday: number;
    globalThresholdAlertsToday: number;
    channelThresholdAlertsToday: number;
    totalExceededAmountToday: ExceededAmountByCurrencyDto[];
    recentLimitExceededEvents: TlmRecentEventDto[];
}

export interface TlmLogItemDto {
    id?: number;
    tenantId: number;
    tenantName?: string;
    channelId?: number;
    channelName?: string;
    currencyCode: string;
    limitType: string;
    logType: string;
    transactionAmount: number;
    usageAmount: number;
    limitAmount: number;
    thresholdAmount: number;
    externalTransactionId: string;
    description: string;
    isNotified: boolean;
    notifiedTime?: string;
    createdTime: string;
}

export interface TlmPagedResultDto {
    totalCount: number;
    items: TlmLogItemDto[];
}

export interface TlmMerchantDetailDto {
    tenantId: number;
    tenantName?: string;
    recentViolations: TlmLogItemDto[];
    usageHistory: TlmUsageHistoryItemDto[];
    latestBlockedTransactions: TlmLogItemDto[];
}

export interface TlmUsageHistoryItemDto {
    date: string;
    currencyCode: string;
    usageAmount: number;
    limitAmount: number;
}

@Injectable()
export class TransactionLimitMonitorServiceProxy {
    private http: HttpClient;
    private baseUrl: string;

    constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.baseUrl = baseUrl !== undefined ? baseUrl : '';
    }

    getDashboard(tenantId?: number): Observable<TlmDashboardDto> {
        let url_ = this.baseUrl + '/api/services/app/TransactionLimitMonitor/GetDashboard';
        if (tenantId != null && tenantId > 0) { url_ += '?tenantId=' + encodeURIComponent('' + tenantId); }
        const options_: any = {
            observe: 'response',
            responseType: 'blob',
            headers: new HttpHeaders({ 'Accept': 'text/plain' })
        };
        return this.http.request('get', url_, options_).pipe(
            _observableMergeMap((response_: any) => this.processResponse<TlmDashboardDto>(response_)),
            _observableCatch((response_: any) => {
                if (response_ instanceof HttpResponseBase) {
                    try { return this.processResponse<TlmDashboardDto>(<any>response_); }
                    catch (e) { return _observableThrow(e) as Observable<TlmDashboardDto>; }
                }
                return _observableThrow(response_) as Observable<TlmDashboardDto>;
            })
        );
    }

    getLogs(
        startDate?: moment.Moment,
        endDate?: moment.Moment,
        tenantId?: number,
        currency?: string,
        logType?: number,
        channelId?: number,
        sorting?: string,
        maxResultCount?: number,
        skipCount?: number
    ): Observable<TlmPagedResultDto> {
        let url_ = this.baseUrl + '/api/services/app/TransactionLimitMonitor/GetLimitExceededLogs?';
        if (startDate != null) { url_ += 'StartDate=' + encodeURIComponent(startDate.toISOString()) + '&'; }
        if (endDate != null) { url_ += 'EndDate=' + encodeURIComponent(endDate.toISOString()) + '&'; }
        if (tenantId != null) { url_ += 'TenantId=' + encodeURIComponent('' + tenantId) + '&'; }
        if (currency != null) { url_ += 'Currency=' + encodeURIComponent(currency) + '&'; }
        if (logType != null) { url_ += 'LogType=' + encodeURIComponent('' + logType) + '&'; }
        if (channelId != null) { url_ += 'ChannelId=' + encodeURIComponent('' + channelId) + '&'; }
        if (sorting != null) { url_ += 'Sorting=' + encodeURIComponent(sorting) + '&'; }
        if (maxResultCount != null) { url_ += 'MaxResultCount=' + encodeURIComponent('' + maxResultCount) + '&'; }
        if (skipCount != null) { url_ += 'SkipCount=' + encodeURIComponent('' + skipCount) + '&'; }
        url_ = url_.replace(/[?&]$/, '');

        const options_: any = {
            observe: 'response',
            responseType: 'blob',
            headers: new HttpHeaders({ 'Accept': 'text/plain' })
        };
        return this.http.request('get', url_, options_).pipe(
            _observableMergeMap((response_: any) => this.processResponse<TlmPagedResultDto>(response_)),
            _observableCatch((response_: any) => {
                if (response_ instanceof HttpResponseBase) {
                    try { return this.processResponse<TlmPagedResultDto>(<any>response_); }
                    catch (e) { return _observableThrow(e) as Observable<TlmPagedResultDto>; }
                }
                return _observableThrow(response_) as Observable<TlmPagedResultDto>;
            })
        );
    }

    getMerchantDetail(tenantId: number): Observable<TlmMerchantDetailDto> {
        const url_ = this.baseUrl + '/api/services/app/TransactionLimitMonitor/GetMerchantDetail?tenantId=' + encodeURIComponent('' + tenantId);
        const options_: any = {
            observe: 'response',
            responseType: 'blob',
            headers: new HttpHeaders({ 'Accept': 'text/plain' })
        };
        return this.http.request('get', url_, options_).pipe(
            _observableMergeMap((response_: any) => this.processResponse<TlmMerchantDetailDto>(response_)),
            _observableCatch((response_: any) => {
                if (response_ instanceof HttpResponseBase) {
                    try { return this.processResponse<TlmMerchantDetailDto>(<any>response_); }
                    catch (e) { return _observableThrow(e) as Observable<TlmMerchantDetailDto>; }
                }
                return _observableThrow(response_) as Observable<TlmMerchantDetailDto>;
            })
        );
    }

    private processResponse<T>(response: HttpResponseBase): Observable<T> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        const _headers: any = {};
        if (response.headers) {
            for (const key of response.headers.keys()) { _headers[key] = response.headers.get(key); }
        }

        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                const result: T = _responseText === '' ? null : JSON.parse(_responseText);
                return _observableOf(result);
            }));
        } else if (status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return _observableThrow({ message: 'An unexpected server error occurred.', status, response: _responseText, headers: _headers });
            }));
        }
        return _observableOf<T>(<any>null);
    }
}
